import React, { useState, useMemo, useEffect } from 'react';
import { Card, Row, Col, Dropdown, Nav, Tab } from 'react-bootstrap';
import { theme } from '../../theme/theme.js';
import './css/DetailView.css';
import EditLayoutModal from './EditLayoutModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import DataApi from '../../api/DataApi';
import TaskActivityTimeline from './TaskActivityTimeline';
import EmailSender from '../common/EmailSender';
import ScoreDisplay from '../common/ScoreDisplay';
import SearchWidget from './SearchWidget';
import Attachment from '../Attachment';
import FilePreviewModal from '../common/FilePreviewModal';
import jwt_decode from 'jwt-decode';
import {
  formatFieldValue as formatFieldValueUtil,
  formatCurrency,
  formatNumber,
  formatDate,
  formatDateTime,
  formatTime,
  formatCalculatedValue,
  normalizeCalculatedDisplayConfig
} from '../../utils/formatUtils';

/**
 * LayoutWidget Component
 * Displays a single widget in card format
 * Supports different widget types (relatedlist, activity, chatter, etc.)
 */
const LayoutWidget = ({ widget, onRefresh, currentRecordId, sourceObjectName, onShowAll }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogActivityModal, setShowLogActivityModal] = useState(false);
  const [logActivityType, setLogActivityType] = useState(null); // 'call', 'meeting', 'note', 'email', 'followup'
  const [showActivityChooser, setShowActivityChooser] = useState(false);
  const [showEmailSender, setShowEmailSender] = useState(false);
  const [recordDataForEmail, setRecordDataForEmail] = useState(null);
  const [loadingRecordData, setLoadingRecordData] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isOtherCollapsed, setIsOtherCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFileRecord, setPreviewFileRecord] = useState(null);

  // Debug: Log when activeTab changes
  useEffect(() => {
    console.log('activeTab state changed to:', activeTab);
  }, [activeTab]);

  // Close activity chooser when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActivityChooser && !event.target.closest('[data-activity-chooser]')) {
        setShowActivityChooser(false);
      }
    };

    if (showActivityChooser) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActivityChooser]);

  // Fetch record data when EmailSender modal opens
  useEffect(() => {
    if (showEmailSender && currentRecordId && sourceObjectName && !recordDataForEmail) {
      setLoadingRecordData(true);
      const dataApi = new DataApi(`object/view/${sourceObjectName}`);
      dataApi.fetchById(currentRecordId)
        .then(response => {
          if (response?.data?.success && response?.data?.data) {
            setRecordDataForEmail(response.data.data);
          } else {
            console.error('Failed to fetch record data for email');
          }
        })
        .catch(error => {
          console.error('Error fetching record data for email:', error);
        })
        .finally(() => {
          setLoadingRecordData(false);
        });
    }

    // Reset recordData when modal closes
    if (!showEmailSender && recordDataForEmail) {
      setRecordDataForEmail(null);
      setLoadingRecordData(false);
    }
  }, [showEmailSender, currentRecordId, sourceObjectName, recordDataForEmail]);

  // Handle tab selection
  const handleTabSelect = (selectedKey) => {
    console.log('Tab selected:', selectedKey, 'Current activeTab:', activeTab);
    if (selectedKey !== activeTab) {
      setActiveTab(selectedKey);
      console.log('ActiveTab updated to:', selectedKey);
    } else {
      console.log('Tab already active, no state change needed');
    }
  };

  // Extract values for useMemo (must be at top level)
  // These will be used conditionally, but hooks must be at top level
  const widgetRecords = widget?.data?.records || [];
  const widgetPicklistField = widget?.config?.picklistField || widget?.data?.picklistField;
  const widgetPicklistValues = widget?.data?.picklistValues || [];
  const widgetMaxRecords = widget?.widgetType === 'relatedlist' && widget?.data
    ? Math.min(widget.config?.maxRecords || 10, 20)
    : 20;

  // Group records by picklist value if picklist field is configured (must be at top level)
  const groupedRecords = useMemo(() => {
    // Only compute if we have a relatedlist widget with data
    if (widget?.widgetType !== 'relatedlist' || !widget?.data || widgetRecords.length === 0) {
      return { 'all': [] };
    }

    if (!widgetPicklistField || !widgetPicklistValues || widgetPicklistValues.length === 0) {
      return { 'all': widgetRecords.slice(0, widgetMaxRecords) };
    }

    // Initialize groups with all picklist values
    const groups = {};
    widgetPicklistValues.forEach(pv => {
      groups[pv.key] = [];
    });
    groups['all'] = []; // Add "All" tab

    // Group records by picklist value
    widgetRecords.forEach(record => {
      const picklistValue = record[widgetPicklistField];
      if (picklistValue && groups[picklistValue]) {
        groups[picklistValue].push(record);
      }
      groups['all'].push(record);
    });

    // Limit records per group to maxRecords
    Object.keys(groups).forEach(key => {
      groups[key] = groups[key].slice(0, widgetMaxRecords);
    });

    return groups;
  }, [widget?.widgetType, widget?.data, widgetRecords, widgetPicklistField, widgetPicklistValues, widgetMaxRecords]);

  if (!widget || !widget.widgetType) return null;

  // Check if this is an Activity Timeline widget
  // Match RelatedTabsModal logic: target_object_name === 'activity' && target_field_name === 'related_record_id' && source_field_name === 'id'
  // It can be either widgetType === 'activity' OR a relatedlist widget with activity target
  const isActivityTimeline = widget.widgetType === 'activity' ||
    (widget.widgetType === 'relatedlist' &&
      widget.data?.targetObjectName === 'activity' &&
      widget.data?.targetFieldName === 'related_record_id' &&
      (widget.data?.sourceFieldName === 'id' || !widget.data?.sourceFieldName)); // sourceFieldName might not always be present

  // For activity widgets, display activity timeline
  if (isActivityTimeline) {
    console.log('LayoutWidget - Activity widget data:', widget?.data);
    console.log('LayoutWidget - Activity widget records:', widget?.data?.records);
    console.log('LayoutWidget - Activity widget records length:', widget?.data?.records?.length);
    if (widget?.data?.records && widget.data.records.length > 0) {
      console.log('LayoutWidget - First record:', widget.data.records[0]);
      console.log('LayoutWidget - First record fields:', widget.data.records[0]?.fields);
    }

    // Check if widget data is still loading (widget.data might be undefined initially)
    const isWidgetLoading = !widget?.data;
    const widgetRecords = widget?.data?.records || [];

    return (
      <>
        <Card className="mb-3 widget-card" style={{ width: '100%' }}>
          <Card.Header
            className="widget-card-header"
            style={{
              padding: '12px 16px',
              borderBottom: isCollapsed ? 'none' : '1px solid #dee2e6',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: theme.colors.primary,
              color: 'rgb(255, 255, 255)'
            }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: 'rgb(255, 255, 255)' }}>
              {widget.widgetLabel || 'Activity Timeline'}
            </h5>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: '0 0 auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Log Activity Chooser */}
              <div style={{ position: 'relative' }} data-activity-chooser>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentRecordId && sourceObjectName) {
                      setShowActivityChooser(!showActivityChooser);
                    }
                  }}
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'rgb(255, 255, 255)',
                    margin: '0px',
                    flex: '0 0 auto',
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Log Activity"
                >
                  <i className="fas fa-plus" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                  Log Activity
                </button>

                {/* Activity Type Chooser Dropdown */}
                {showActivityChooser && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '180px',
                      padding: '4px 0'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        padding: '8px 16px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#666',
                        borderBottom: '1px solid #dee2e6',
                        marginBottom: '4px'
                      }}
                    >
                      Log Activity
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('call');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-phone" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Call</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('meeting');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-calendar" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Meeting</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('note');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-sticky-note" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Add Note</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setShowActivityChooser(false);
                        // Show modal immediately
                        if (currentRecordId && sourceObjectName) {
                          setShowEmailSender(true);
                        }
                      }}
                    >
                      <i className="fas fa-envelope" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Log Email</span>
                    </div>
                    <div
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#333'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => {
                        setLogActivityType('followup');
                        setShowActivityChooser(false);
                        setShowLogActivityModal(true);
                      }}
                    >
                      <i className="fas fa-clock" style={{ color: '#0176d3', width: '16px' }}></i>
                      <span>Add Follow-up</span>
                    </div>
                  </div>
                )}
              </div>
              <i
                className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}
                style={{
                  color: 'rgb(255, 255, 255)',
                  fontSize: '0.875rem',
                  transition: 'transform 0.2s ease'
                }}
              />
            </div>
          </Card.Header>
          {!isCollapsed && (
            <Card.Body style={{ padding: '0', maxHeight: widget.config?.maxHeight || 600, overflowY: 'auto' }}>
              {isWidgetLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                  Loading activities...
                </div>
              ) : (
                <TaskActivityTimeline
                  recordId={currentRecordId}
                  objectName={sourceObjectName}
                  onRefresh={onRefresh}
                  hideLogActivityButton={true}
                  records={widgetRecords}
                />
              )}
            </Card.Body>
          )}
        </Card>

        {/* Email Sender Modal */}
        {showEmailSender && sourceObjectName && (
          <EmailSender
            show={showEmailSender}
            onHide={() => {
              setShowEmailSender(false);
              setRecordDataForEmail(null);
              setLoadingRecordData(false);
            }}
            recordData={recordDataForEmail} // Can be null while loading
            objectName={sourceObjectName}
            currentUser={(() => {
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  const decoded = jwt_decode(token);
                  return {
                    id: decoded.id,
                    firstname: decoded.firstname,
                    lastname: decoded.lastname,
                    username: decoded.username,
                    email: decoded.email,
                  };
                }
              } catch (error) {
                console.error('Failed to decode current user token:', error);
              }
              return null;
            })()}
            onEmailSent={(emailData) => {
              console.log('Email sent successfully:', emailData);
              setShowEmailSender(false);
              setRecordDataForEmail(null);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
          />
        )}

        {/* Log Activity Modal */}
        {showLogActivityModal && currentRecordId && sourceObjectName && logActivityType && (
          <EditLayoutModal
            show={showLogActivityModal}
            onHide={() => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
            }}
            objectName={
              logActivityType === 'note' ? 'note' :
                logActivityType === 'email' ? 'email_log' :
                  'task' // call, meeting, followup all use 'task'
            }
            id={null}
            sourcePage="detail"
            onSave={(savedData) => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
            onCancel={() => {
              setShowLogActivityModal(false);
              setLogActivityType(null);
            }}
            onRefresh={onRefresh}
            queryParams={
              logActivityType === 'note' ? {
                targetField: 'parent_id', // Notes use parent_id instead of related_record_id
                targetId: currentRecordId,
                parentObjectName: sourceObjectName
              } :
                logActivityType === 'email' ? {
                  targetField: 'related_record_id',
                  targetId: currentRecordId,
                  parentObjectName: sourceObjectName
                } :
                  {
                    targetField: 'related_record_id',
                    targetId: currentRecordId,
                    parentObjectName: sourceObjectName,
                    taskType: logActivityType === 'call' ? 'Call' :
                      logActivityType === 'meeting' ? 'Meeting' :
                        logActivityType === 'followup' ? 'Follow-up' : 'Call'
                  }
            }
          />
        )}
      </>
    );
  }

  // For score widgets, display ScoreDisplay component
  if (widget.widgetType === 'score') {
    const scoreMaxHeightValue = widget.config?.max_height ?? widget.config?.maxHeight;
    const scoreMaxHeight = scoreMaxHeightValue !== undefined && scoreMaxHeightValue !== null
      ? (typeof scoreMaxHeightValue === 'string' ? parseInt(scoreMaxHeightValue, 10) : scoreMaxHeightValue)
      : 400;
    return (
      <Card className="mb-3 widget-card" style={{ width: '100%', maxHeight: `${scoreMaxHeight}px`, display: 'flex', flexDirection: 'column' }}>
        <Card.Body style={{ padding: 0, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <ScoreDisplay objectName={sourceObjectName} recordId={currentRecordId} />
        </Card.Body>
      </Card>
    );
  }

  // For search widgets, display SearchWidget component
  if (widget.widgetType === 'search') {
    // Get recordData from parent - we need to pass it as a prop
    // For now, we'll fetch it if not provided
    return (
      <SearchWidget
        widget={widget}
        currentRecordId={currentRecordId}
        sourceObjectName={sourceObjectName}
        recordData={widget.recordData} // Pass recordData if available
      />
    );
  }

  // For relatedlist widgets, display records in card style
  // Skip Activity Timeline widgets as they're handled above
  if (widget.widgetType === 'relatedlist' && widget.data && !isActivityTimeline) {
    const records = widget.data.records || [];
    const targetObjectName = widget.data.targetObjectName || widget.config?.targetObjectName;
    // widget.data.fields is now an array of {fieldName, fieldLabel, dataType}
    // Fallback to widget.config.fields (array of field names) for backward compatibility
    const fieldMetadata = widget.data.fields || widget.config?.fields?.map(fieldName => ({ fieldName, fieldLabel: fieldName })) || [];
    console.log('LayoutWidget - fieldMetadata:', JSON.stringify(fieldMetadata, null, 2));
    console.log('LayoutWidget - widget.data:', widget.data);
    console.log('LayoutWidget - widget.config:', widget.config);
    const maxRecords = Math.min(widget.config?.maxRecords || 10, 20); // Max 20 records
    // Parse maxHeight - handle both number and string, ensure it's a valid number
    const maxHeightValue = widget.config?.maxHeight;
    const maxHeight = maxHeightValue !== undefined && maxHeightValue !== null
      ? (typeof maxHeightValue === 'string' ? parseInt(maxHeightValue, 10) : maxHeightValue)
      : 400;

    // Picklist field configuration
    const picklistField = widget.config?.picklistField || widget.data?.picklistField;
    const picklistValues = widget.data?.picklistValues || [];

    // Helper to normalize picklist values that may come as objects
    const normalizePicklistValue = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value !== 'object' || Array.isArray(value)) return value;
      if (value.value !== undefined && value.value !== null) return value.value;
      if (value.displayValue !== undefined && value.displayValue !== null) return value.displayValue;
      return null;
    };

    // Recompute groupedRecords with actual records from this block
    // (useMemo at top level might have computed with empty array, so recompute here)
    let actualGroupedRecords = {};
    if (records.length > 0 && picklistField && picklistValues && picklistValues.length > 0) {
      // Initialize groups with all picklist values
      picklistValues.forEach(pv => {
        actualGroupedRecords[pv.key] = [];
      });
      actualGroupedRecords['all'] = []; // Add "All" tab

      // Group records by picklist value
      records.forEach(record => {
        const picklistValue = normalizePicklistValue(record[picklistField]);
        const picklistKey = picklistValue !== null && picklistValue !== undefined ? String(picklistValue) : null;
        console.log('Grouping record:', { picklistField, picklistValue, record });

        // Add to specific picklist group if it matches
        if (picklistKey && actualGroupedRecords[picklistKey]) {
          actualGroupedRecords[picklistKey].push(record);
        }
        // Always add to "all" group
        actualGroupedRecords['all'].push(record);
      });

      // Limit records per group to maxRecords
      Object.keys(actualGroupedRecords).forEach(key => {
        actualGroupedRecords[key] = actualGroupedRecords[key].slice(0, maxRecords);
      });

      console.log('Final groupedRecords:', actualGroupedRecords);
    } else if (records.length > 0) {
      // No picklist grouping, just use all records
      actualGroupedRecords = { 'all': records.slice(0, maxRecords) };
      console.log('No picklist - using all records:', actualGroupedRecords);
    } else {
      actualGroupedRecords = { 'all': [] };
      console.log('No records available');
    }

    // Debug logging
    console.log('LayoutWidget - Debug:', {
      recordsCount: records.length,
      picklistField,
      picklistValuesCount: picklistValues.length,
      actualGroupedRecords,
      activeTab,
      maxRecords,
      displayRecordsCount: picklistField && picklistValues.length > 0
        ? (actualGroupedRecords[activeTab] || []).length
        : records.slice(0, maxRecords).length
    });

    const displayRecords = picklistField && picklistValues.length > 0
      ? (actualGroupedRecords[activeTab] || [])
      : records.slice(0, maxRecords);

    // Helper to get record ID value - must be defined before use in map
    const getRecordIdValue = (record) => {
      if (!record) return null;
      if (record.id && typeof record.id === 'string') {
        return record.id;
      }
      if (record.id && typeof record.id === 'object' && record.id.value) {
        return record.id.value;
      }
      return record.id || null;
    };

    // Resolve currency with priority: token currency (user or company) > default
    const resolveCurrency = (fallbackCurrency = 'INR') => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = jwt_decode(token);
          if (decoded?.currency) {
            return decoded.currency;
          }
        }
      } catch (error) {
        console.error('Error decoding token for currency:', error);
      }
      return fallbackCurrency;
    };

    // Helper function to safely extract primitive value from potentially nested objects
    const extractPrimitiveValue = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      // If it's already a primitive (not an object or array), return it
      if (typeof value !== 'object' || Array.isArray(value)) {
        return value;
      }

      // If it's an object, try to extract a primitive value
      // Priority: value > displayValue > convert to string
      if (value.value !== undefined) {
        // Recursively extract if value is also an object
        const extracted = extractPrimitiveValue(value.value);
        if (extracted !== null) return extracted;
      }

      if (value.displayValue !== undefined) {
        const extracted = extractPrimitiveValue(value.displayValue);
        if (extracted !== null) return extracted;
      }

      // If we can't extract a primitive, convert to string as last resort
      try {
        const stringValue = String(value);
        return stringValue === '[object Object]' ? null : stringValue;
      } catch {
        return null;
      }
    };

    // Helper function to get currency from record
    const getRecordCurrency = (record) => {
      if (!record) return null;

      const currencyField = record.currency || record.currency_code || record.Currency;
      if (currencyField) {
        return extractPrimitiveValue(currencyField);
      }

      if (Array.isArray(record.fields)) {
        const currencyMeta = record.fields.find(field =>
          String(field?.fieldName || '').toLowerCase() === 'currency'
        );
        if (currencyMeta) {
          const currencyValue = currencyMeta.value ?? currencyMeta.displayValue;
          const extracted = extractPrimitiveValue(currencyValue);
          if (extracted) {
            return extracted;
          }
        }
      }

      return null;
    };

    // Check if this is an attachment widget
    const isAttachmentWidget = targetObjectName && (
      targetObjectName.toLowerCase() === 'attachment' ||
      widget.widgetLabel?.toLowerCase().includes('attachment')
    );

    // Action handlers
    const handleViewAttachment = (record) => {
      console.log('handleViewAttachment called with record:', record);
      setPreviewFileRecord(record);
      setShowPreviewModal(true);
      setActiveDropdown(null);
    };

    const handleViewRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId || !targetObjectName) return;

      // For attachments, show preview instead of navigating to detail page
      if (isAttachmentWidget) {
        handleViewAttachment(record);
      } else {
        window.open(`/view/${targetObjectName}/${recordId}`, '_blank');
      }
    };

    const handleEditRecord = (record) => {
      // Don't allow editing attachments
      if (isAttachmentWidget) return;

      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowEditModal(true);
      setActiveDropdown(null);
    };

    const handleCloneRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowCloneModal(true);
      setActiveDropdown(null);
    };

    const handleDeleteRecord = (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setRecordToDelete(record);
      setShowDeleteModal(true);
      setActiveDropdown(null);
    };

    const confirmDeleteRecord = async () => {
      if (!recordToDelete || !targetObjectName) return;

      const recordId = getRecordIdValue(recordToDelete);
      if (!recordId) return;

      try {
        const dataApi = new DataApi('object');
        const response = await dataApi.delete(`/${targetObjectName}/${recordId}`);

        if (response.data.success) {
          // Refresh the widget data
          if (onRefresh) {
            onRefresh();
          }
          setShowDeleteModal(false);
          setRecordToDelete(null);
        } else {
          alert(`Failed to delete record: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        alert(`Error deleting record: ${error.message}`);
      }
    };

    const handleModalSaveSuccess = () => {
      setShowEditModal(false);
      setShowCloneModal(false);
      setEditingRecord(null);
      // Refresh the widget data
      if (onRefresh) {
        onRefresh();
      }
    };

    const handleModalCancel = () => {
      setShowEditModal(false);
      setShowCloneModal(false);
      setEditingRecord(null);
    };

    // Helper function to render record cards
    const renderRecordCards = (recordsToRender, totalRecordsCount = null) => {
      console.log('renderRecordCards called with:', {
        count: recordsToRender?.length || 0,
        totalRecords: totalRecordsCount,
        records: recordsToRender,
        isArray: Array.isArray(recordsToRender),
        firstRecord: recordsToRender?.[0]
      });

      if (!recordsToRender || !Array.isArray(recordsToRender) || recordsToRender.length === 0) {
        console.log('renderRecordCards: No records to render');
        return (
          <p className="text-muted mb-0" style={{ fontSize: theme.typography.fontSize.lg }}>
            No records found
          </p>
        );
      }

      console.log('renderRecordCards: Rendering', recordsToRender.length, 'records');
      console.log('renderRecordCards: fieldMetadata available?', fieldMetadata?.length > 0, fieldMetadata);
      return (
        <div className="widget-records" style={{ width: '100%' }}>
          {recordsToRender.map((record, idx) => {
            const recordId = getRecordIdValue(record);
            const recordIdValue = recordId || `record-${idx}`;
            console.log(`renderRecordCards: Rendering record ${idx}:`, record);

            return (
              <Card key={idx} className="mb-2 widget-record-card" style={{ border: '1px solid #e0e0e0', width: '100%', position: 'relative' }}>
                <Card.Body className="p-3">
                  {/* Actions Dropdown - Top Right Corner */}
                  <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                    <Dropdown
                      onToggle={(isOpen) => {
                        setActiveDropdown(isOpen ? recordIdValue : null);
                      }}
                      show={activeDropdown === recordIdValue}
                      className="widget-actions-dropdown"
                    >
                      <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        id={`widget-dropdown-${recordIdValue}`}
                        className="widget-card-dropdown-toggle"
                        style={{
                          padding: '2px 8px',
                          fontSize: '14px',
                          border: '1px solid #dee2e6',
                          backgroundColor: 'white',
                          color: '#6c757d',
                          lineHeight: 1,
                          opacity: activeDropdown === recordIdValue ? 1 : 0,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          if (activeDropdown !== recordIdValue) {
                            e.target.style.opacity = '0';
                          }
                        }}
                      >
                        <span style={{ color: '#6c757d', fontWeight: 'bold' }}>&#9660;</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end" className="widget-dropdown-menu">
                        {isAttachmentWidget ? (
                          <>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewAttachment(record);
                              }}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                const recordId = getRecordIdValue(record);
                                if (recordId) {
                                  const dataApi = new DataApi('attachments');
                                  dataApi.get(`/download/${recordId}`, { responseType: 'blob' })
                                    .then(response => {
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      const fileName = record.file_name?.value || record.file_name || 'download';
                                      link.setAttribute('download', fileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    })
                                    .catch(error => {
                                      console.error('Error downloading file:', error);
                                      alert('Failed to download file');
                                    });
                                }
                              }}
                            >
                              <i className="fas fa-download me-2"></i>
                              Download
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRecord(record);
                              }}
                              className="text-danger"
                            >
                              <i className="fas fa-trash me-2"></i>
                              Delete
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRecord(record);
                              }}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditRecord(record);
                              }}
                            >
                              <i className="fas fa-edit me-2"></i>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCloneRecord(record);
                              }}
                            >
                              <i className="fas fa-clone me-2"></i>
                              Clone
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRecord(record);
                              }}
                              className="text-danger"
                            >
                              <i className="fas fa-trash me-2"></i>
                              Delete
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <Row className="g-2" style={{ paddingRight: '30px' }}>
                    {fieldMetadata && fieldMetadata.length > 0 ? (() => {
                      // Filter out 'id' field from display
                      const filteredFields = fieldMetadata.filter(fieldMeta => {
                        const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                        return fieldName?.toLowerCase() !== 'id';
                      });

                      // Only apply special title/description layout for notes
                      const isNotesWidget = targetObjectName && (
                        targetObjectName.toLowerCase() === 'note' ||
                        targetObjectName.toLowerCase() === 'notes'
                      );

                      // Find title/name and description fields (only for notes)
                      const titleFieldNames = ['name', 'title', 'subject'];
                      const descriptionFieldName = 'description';

                      let titleField = null;
                      let descriptionField = null;
                      const otherFields = [];

                      if (isNotesWidget) {
                        // For notes, separate title and description for special layout
                        filteredFields.forEach(fieldMeta => {
                          const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                          const fieldNameLower = fieldName?.toLowerCase();

                          if (titleFieldNames.includes(fieldNameLower) && !titleField) {
                            titleField = fieldMeta;
                          } else if (fieldNameLower === descriptionFieldName && !descriptionField) {
                            descriptionField = fieldMeta;
                          } else {
                            otherFields.push(fieldMeta);
                          }
                        });
                      } else {
                        // For other widgets (like tasks), treat all fields equally in 2-column layout
                        otherFields.push(...filteredFields);
                      }

                      // Format file size helper function
                      const formatFileSize = (bytes) => {
                        if (!bytes || bytes === 0) return '0 Bytes';
                        // Handle both number and string inputs
                        const sizeInBytes = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
                        if (isNaN(sizeInBytes) || sizeInBytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
                        return parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                      };

                      const renderField = (fieldMeta, colSize = 6) => {
                        // Handle both object format {fieldName, fieldLabel} and string format
                        const fieldName = typeof fieldMeta === 'object' ? fieldMeta.fieldName : fieldMeta;
                        const fieldLabel = typeof fieldMeta === 'object' ? fieldMeta.fieldLabel : fieldMeta;
                        const dataType = typeof fieldMeta === 'object' ? fieldMeta.dataType : 'text';
                        const referenceObject = typeof fieldMeta === 'object' ? fieldMeta.referenceObject : null;

                        // Check if this is a file size field
                        const isFileSizeField = fieldName && (
                          fieldName.toLowerCase() === 'file_size' ||
                          fieldName.toLowerCase() === 'filesize' ||
                          fieldName.toLowerCase() === 'size' ||
                          fieldName.toLowerCase().endsWith('_size')
                        );

                        // Get field value - handle both plain value and enriched lookup object
                        const fieldData = record[fieldName];
                        let fieldValue = '—';
                        let displayValue = null;
                        let isLookup = false;
                        let isRichText = false;
                        let isUrl = false;
                        let lookupReferenceObject = null;
                        let lookupId = null;
                        const normalizedType = typeof dataType === 'string' ? dataType.toLowerCase() : '';
                        const recordCurrency = getRecordCurrency(record);

                        const getDisplayConfig = () => {
                          return fieldData?.displayConfig ??
                            fieldData?.display_config ??
                            fieldMeta?.displayConfig ??
                            fieldMeta?.display_config ??
                            null;
                        };

                        const getCurrencyCode = () => {
                          return recordCurrency ||
                            fieldData?.currencyCode ||
                            fieldMeta?.currencyCode ||
                            resolveCurrency('INR');
                        };

                        // Check if field is richtext type
                        const fieldType = typeof fieldData === 'object' && fieldData !== null ? fieldData.type : null;
                        isRichText = dataType === 'richtext' || fieldType === 'richtext';
                        isUrl = dataType === 'url' || fieldType === 'url';

                        if (fieldData !== null && fieldData !== undefined) {
                          // Check if it's an enriched lookup field object
                          if (typeof fieldData === 'object' && fieldData.isReference) {
                            isLookup = true;
                            lookupId = extractPrimitiveValue(fieldData.value);
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            displayValue = extractedDisplayValue || extractedValue;
                            lookupReferenceObject = fieldData.referenceObject || referenceObject;
                            fieldValue = displayValue || '—';
                          } else if (typeof fieldData === 'object' && (fieldData.isPicklist || fieldData.isMultiPicklist)) {
                            // Handle picklist fields - use displayValue if available, otherwise value
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            fieldValue = extractedDisplayValue || extractedValue || '—';
                          } else if (dataType === 'lookup' && referenceObject && fieldData) {
                            // Fallback: if it's a lookup field but not enriched, treat it as lookup
                            isLookup = true;
                            lookupId = extractPrimitiveValue(fieldData);
                            displayValue = extractPrimitiveValue(fieldData);
                            lookupReferenceObject = referenceObject;
                            fieldValue = displayValue || '—';
                          } else if ((dataType === 'picklist' || dataType === 'multipicklist') && typeof fieldData === 'object' && fieldData !== null) {
                            // Handle picklist fields that might be objects from fetchRecords
                            const extractedDisplayValue = extractPrimitiveValue(fieldData.displayValue);
                            const extractedValue = extractPrimitiveValue(fieldData.value);
                            fieldValue = extractedDisplayValue || extractedValue || '—';
                          } else if (normalizedType === 'currency') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const numValue = Number(rawValue);
                            const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                              (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                              (fieldData?.scale ?? fieldMeta?.scale) !== ''
                              ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                              : 2;
                            if (!Number.isNaN(numValue)) {
                              fieldValue = formatCurrency(numValue, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else {
                              fieldValue = formatCurrency(0, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            }
                          } else if (normalizedType === 'rollupsummary') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const displayConfig = getDisplayConfig();
                            let parsedConfig = {};
                            if (displayConfig) {
                              if (typeof displayConfig === 'string') {
                                try {
                                  parsedConfig = JSON.parse(displayConfig);
                                } catch {
                                  parsedConfig = {};
                                }
                              } else {
                                parsedConfig = displayConfig || {};
                              }
                            }
                            const rollupConfig = parsedConfig.rollupSummary || {};
                            const outputType = (rollupConfig.outputType || 'number').toLowerCase();
                            const numValue = Number(rawValue);
                            if (outputType === 'currency') {
                              const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== ''
                                ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                                : 2;
                              const safeValue = Number.isNaN(numValue) ? 0 : numValue;
                              fieldValue = formatCurrency(safeValue, getCurrencyCode(), {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else if (outputType === 'date') {
                              fieldValue = rawValue ? formatDate(rawValue) : '';
                            } else if (outputType === 'datetime') {
                              fieldValue = rawValue ? formatDateTime(rawValue) : '';
                            } else if (outputType === 'time') {
                              fieldValue = rawValue ? formatTime(rawValue) : '';
                            } else if (!Number.isNaN(numValue)) {
                              const decimalPlaces = (fieldData?.scale ?? fieldMeta?.scale) !== undefined &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== null &&
                                (fieldData?.scale ?? fieldMeta?.scale) !== ''
                                ? parseInt(fieldData?.scale ?? fieldMeta?.scale, 10)
                                : undefined;
                              fieldValue = formatNumber(numValue, {
                                minimumFractionDigits: decimalPlaces,
                                maximumFractionDigits: decimalPlaces
                              });
                            } else {
                              fieldValue = '';
                            }
                          } else if (normalizedType === 'calculated') {
                            const rawValue = extractPrimitiveValue(fieldData);
                            const displayConfig = getDisplayConfig();
                            const normalized = normalizeCalculatedDisplayConfig(displayConfig);
                            if (normalized.returnType === 'currency') {
                              const decimalPlaces = normalized.decimalPlaces ?? 2;
                              const numValue = Number(rawValue);
                              const safeValue = Number.isNaN(numValue) ? 0 : numValue;
                              const currencyFromConfig = typeof normalized.currencySymbol === 'string'
                                ? normalized.currencySymbol.trim()
                                : '';

                              if (currencyFromConfig && !/^[A-Z]{3}$/.test(currencyFromConfig)) {
                                fieldValue = formatCalculatedValue(safeValue, displayConfig);
                              } else {
                                fieldValue = formatCurrency(safeValue, currencyFromConfig || getCurrencyCode(), {
                                  minimumFractionDigits: decimalPlaces,
                                  maximumFractionDigits: decimalPlaces
                                });
                              }
                            } else {
                              fieldValue = formatCalculatedValue(rawValue, displayConfig);
                            }
                          } else if (isRichText) {
                            // Rich text field - get the HTML value
                            const extractedValue = extractPrimitiveValue(fieldData);
                            fieldValue = extractedValue !== null ? extractedValue : fieldData;
                          } else if (isUrl) {
                            // URL field - extract the URL value
                            const extractedValue = extractPrimitiveValue(fieldData);
                            fieldValue = extractedValue !== null ? extractedValue : '—';
                          } else if (isFileSizeField) {
                            // File size field - format as KB/MB
                            const extractedValue = extractPrimitiveValue(fieldData);
                            if (extractedValue !== null && extractedValue !== undefined && extractedValue !== '—') {
                              fieldValue = formatFileSize(extractedValue);
                            } else {
                              fieldValue = '—';
                            }
                          } else {
                            // Regular field value - extract primitive value
                            const rawValue = extractPrimitiveValue(fieldData);
                            const isNumericType = ['currency', 'number', 'integer', 'decimal', 'rollupsummary'].includes(normalizedType);

                            // Create a field object for formatFieldValueUtil (matches DetailLayout format)
                            const fieldForFormatting = {
                              fieldName: fieldName,
                              label: fieldLabel,
                              type: dataType,
                              value: rawValue,
                              displayValue: typeof fieldData === 'object' && fieldData !== null ? fieldData.displayValue : null,
                              scale: typeof fieldData === 'object' && fieldData !== null ? fieldData.scale : null,
                              precision: typeof fieldData === 'object' && fieldData !== null ? fieldData.precision : null,
                              displayConfig: typeof fieldData === 'object' && fieldData !== null ? fieldData.displayConfig : null
                            };

                            // Use formatFieldValueUtil for consistent formatting (handles dates, numbers, currency, etc.)
                            // This matches how DetailLayout formats field values
                            if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                              const formatted = formatFieldValueUtil(fieldForFormatting);
                              fieldValue = formatted !== null && formatted !== undefined ? formatted : '—';
                            } else {
                              if (isNumericType) {
                                const formattedZero = formatFieldValueUtil({ ...fieldForFormatting, value: 0 });
                                fieldValue = formattedZero !== null && formattedZero !== undefined ? formattedZero : '0';
                              } else {
                                fieldValue = '';
                              }
                            }
                          }
                        }

                        return (
                          <Col key={fieldName} xs={colSize} className="widget-field mb-2">
                            <div
                              className="d-block mb-1"
                              style={{
                                fontFamily: theme.typography.fontFamily.primary,
                                fontWeight: theme.typography.fontWeight.medium,
                                color: theme.colors.textSecondary,
                                lineHeight: theme.typography.lineHeight.normal,
                                fontSize: theme.typography.fontSize.lg
                              }}
                            >
                              {fieldLabel}
                            </div>
                            <div
                              style={{
                                fontFamily: theme.typography.fontFamily.primary,
                                fontWeight: theme.typography.fontWeight.normal,
                                color: theme.colors.textPrimary,
                                lineHeight: theme.typography.lineHeight.normal,
                                fontSize: theme.typography.fontSize.lg,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                              }}
                              className={isRichText ? 'richtext-value rich-text-content' : ''}
                              title={fieldValue && fieldValue !== '—' ? String(fieldValue) : ''}
                            >
                              {isRichText ? (
                                <div dangerouslySetInnerHTML={{ __html: fieldValue || '—' }} />
                              ) : isUrl && fieldValue && fieldValue !== '—' ? (
                                <a
                                  href={fieldValue}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-brand"
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    textDecoration: 'none',
                                    transition: theme.transitions.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                  }}
                                  title={fieldValue}
                                >
                                  {fieldValue}
                                </a>
                              ) : isLookup && lookupReferenceObject && lookupId ? (
                                <a
                                  href={`/view/${lookupReferenceObject}/${lookupId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link-brand"
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    textDecoration: 'none',
                                    transition: theme.transitions.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                  }}
                                  title={fieldValue}
                                >
                                  {fieldValue}
                                </a>
                              ) : (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={fieldValue && fieldValue !== '—' ? String(fieldValue) : ''}
                                >
                                  {fieldValue}
                                </span>
                              )}
                            </div>
                          </Col>
                        );
                      };

                      return (
                        <>
                          {/* For notes: Title/Name field - Full width row */}
                          {isNotesWidget && titleField && renderField(titleField, 12)}

                          {/* For notes: Description field - Full width row */}
                          {isNotesWidget && descriptionField && renderField(descriptionField, 12)}

                          {/* Other fields - 2 column grid (for all widgets, including title/description for non-notes) */}
                          {otherFields.map((fieldMeta, fieldIdx) => renderField(fieldMeta, 6))}
                        </>
                      );
                    })() : (
                      <Col xs={12}>
                        <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
                          No fields configured for this widget
                        </p>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
          {totalRecordsCount && totalRecordsCount > maxRecords && (
            <div className="text-center mt-2">
              <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                Showing {Math.min(recordsToRender.length, maxRecords)} of {totalRecordsCount} records
              </small>
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        <Card className="mb-3 widget-card" style={{ width: '100%', maxHeight: isCollapsed ? 'auto' : `${maxHeight}px`, display: 'flex', flexDirection: 'column' }}>
          <Card.Header
            className="widget-card-header"
            style={{
              padding: '12px 16px',
              borderBottom: isCollapsed ? 'none' : '1px solid #dee2e6',
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%'
            }}
          >
            {/* Title */}
            <h5
              className="mb-0"
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#ffffff',
                margin: 0,
                flex: '0 0 auto'
              }}
            >
              {widget.widgetLabel || 'Related List'}
            </h5>

            {/* Action Buttons and Arrow Container */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: '0 0 auto'
              }}
            >
              {/* Action Buttons - Before Arrow */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flex: '0 0 auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Create Record / Upload Button */}
                {targetObjectName && (targetObjectName.toLowerCase() === 'attachment' ||
                  widget.widgetLabel?.toLowerCase().includes('attachment')) ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAttachmentModal(true);
                    }}
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'rgb(255, 255, 255)',
                      margin: '0px',
                      flex: '0 0 auto',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Upload Attachment"
                  >
                    <i className="fas fa-upload" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                    Upload
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (targetObjectName) {
                        // Get target_field_name from widget data (now included by backend)
                        const targetFieldName = widget.data?.targetFieldName ||
                          widget.config?.targetFieldName ||
                          widget.config?.target_field_name;
                        // Store it in widget config for use in Create Modal
                        if (targetFieldName && !widget.config?.targetFieldName) {
                          widget.config = widget.config || {};
                          widget.config.targetFieldName = targetFieldName;
                        }
                        setShowCreateModal(true);
                      }
                    }}
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: 'rgb(255, 255, 255)',
                      margin: '0px',
                      flex: '0 0 auto',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Create New Record"
                  >
                    <i className="fas fa-plus" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                    Create Record
                  </button>
                )}
                {/* Show All Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!onShowAll || !targetObjectName) return;

                    // Get target_field_name from widget data (now included by backend)
                    // Priority: widget.data.targetFieldName > widget.config.targetFieldName > widget.config.target_field_name
                    const targetFieldName = widget.data?.targetFieldName ||
                      widget.config?.targetFieldName ||
                      widget.config?.target_field_name;

                    if (!targetFieldName) {
                      console.error('❌ Cannot show related records: target_field_name is missing from widget data');
                      alert('Error: Cannot determine relationship field. Please contact support.');
                      return;
                    }

                    // Get actions from widget data (from related list config) or widget config
                    const actions = widget.data?.actions ||
                      widget.config?.actions ||
                      ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];

                    // Get fields from widget config - ensure "id" is included for backend but will be filtered from display
                    let displayFields = widget.config?.fields || ['name'];
                    // Ensure "id" is included in fields sent to backend (it will be filtered out from display automatically)
                    if (!displayFields.includes('id')) {
                      displayFields = ['id', ...displayFields];
                    }

                    // Create a tab-like object similar to RelatedTabsModal structure
                    const tabObject = {
                      tabName: widget.widgetLabel || 'Related List',
                      config: {
                        target_object_name: targetObjectName,
                        target_field_name: targetFieldName,
                        actions: actions,
                        field_config: {
                          fields: displayFields
                        }
                      }
                    };

                    console.log('📋 Created tab object for Show All:', tabObject);
                    onShowAll(tabObject, currentRecordId, sourceObjectName);
                  }}
                  style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'rgb(255, 255, 255)',
                    margin: '0px',
                    flex: '0 0 auto',
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Show All Records"
                >
                  <i className="fas fa-list" style={{ color: 'rgb(255, 255, 255)', fontSize: '1rem', fontWeight: 600, margin: '0px', flex: '0 0 auto' }}></i>
                  Show All
                </button>
              </div>

              {/* Collapse Arrow */}
              <i
                className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`}
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'rgb(255, 255, 255)',
                  margin: '0px',
                  flex: '0 0 auto',
                  transition: 'transform 0.2s ease',
                  cursor: 'pointer'
                }}
              />
            </div>
          </Card.Header>
          {!isCollapsed && (
            <Card.Body style={{ padding: '12px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {picklistField && picklistValues && picklistValues.length > 0 ? (
                <Tab.Container
                  id={`widget-tabs-${widget.widgetId}`}
                  activeKey={activeTab}
                  onSelect={handleTabSelect}
                >
                  <Nav variant="tabs" className="mb-3 widget-tabs-nav" style={{ borderBottom: '2px solid #dee2e6' }}>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="all"
                        style={{
                          color: activeTab === 'all' ? theme.colors.primary : '#495057',
                          fontWeight: activeTab === 'all' ? 600 : 500,
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          borderBottom: activeTab === 'all' ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
                          padding: '8px 16px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        All ({records.length})
                      </Nav.Link>
                    </Nav.Item>
                    {picklistValues.map((pv) => {
                      const count = actualGroupedRecords[pv.key]?.length || 0;
                      const isActive = activeTab === pv.key;
                      return (
                        <Nav.Item key={pv.key}>
                          <Nav.Link
                            eventKey={pv.key}
                            style={{
                              color: isActive ? theme.colors.primary : '#495057',
                              fontWeight: isActive ? 600 : 500,
                              cursor: 'pointer',
                              backgroundColor: 'transparent',
                              borderBottom: isActive ? `3px solid ${theme.colors.primary}` : '3px solid transparent',
                              padding: '8px 16px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {pv.label} ({count})
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                  <Tab.Content className="widget-tab-content" style={{ minHeight: '200px', paddingTop: '10px' }}>
                    <Tab.Pane
                      eventKey="all"
                      transition={false}
                      className={activeTab === 'all' ? 'active' : ''}
                      data-tab-name="all"
                      data-is-active={activeTab === 'all'}
                    >
                      {(() => {
                        const allRecords = actualGroupedRecords['all'] || [];
                        console.log('All tab - Rendering with records:', allRecords.length, 'activeTab:', activeTab, 'className:', activeTab === 'all' ? 'active' : '');
                        if (allRecords.length === 0) {
                          return <p className="text-muted">No records in All tab</p>;
                        }
                        return renderRecordCards(allRecords, records.length);
                      })()}
                    </Tab.Pane>
                    {picklistValues.map((pv) => {
                      const tabRecords = actualGroupedRecords[pv.key] || [];
                      const totalForTab = records.filter(r => {
                        const normalized = normalizePicklistValue(r[picklistField]);
                        return normalized !== null && String(normalized) === String(pv.key);
                      }).length;
                      const isActive = activeTab === pv.key;
                      console.log(`Tab ${pv.key} - Rendering with records:`, tabRecords.length, 'of', totalForTab, 'activeTab:', activeTab, 'isActive:', isActive, 'className:', isActive ? 'active' : '');
                      return (
                        <Tab.Pane
                          key={pv.key}
                          eventKey={pv.key}
                          transition={false}
                          className={isActive ? 'active' : ''}
                          data-tab-name={pv.key}
                          data-is-active={isActive}
                        >
                          {tabRecords.length === 0 ? (
                            <p className="text-muted">No records in {pv.label} tab</p>
                          ) : (
                            renderRecordCards(tabRecords, totalForTab)
                          )}
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </Tab.Container>
              ) : (
                renderRecordCards(displayRecords)
              )}
            </Card.Body>
          )}
        </Card>

        {/* Edit Modal */}
        {showEditModal && editingRecord && targetObjectName && (
          <EditLayoutModal
            show={showEditModal}
            onHide={handleModalCancel}
            objectName={targetObjectName}
            id={editingRecord.id?.value}
            sourcePage="detail"
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
          />
        )}

        {/* Clone Modal */}
        {showCloneModal && editingRecord && targetObjectName && (
          <EditLayoutModal
            show={showCloneModal}
            onHide={handleModalCancel}
            objectName={targetObjectName}
            id={editingRecord.id?.value}
            isClone={true}
            sourcePage="detail"
            onSave={handleModalSaveSuccess}
            onCancel={handleModalCancel}
            onRefresh={onRefresh}
          />
        )}

        {/* Create Modal */}
        {showCreateModal && targetObjectName && (
          <EditLayoutModal
            show={showCreateModal}
            onHide={() => {
              setShowCreateModal(false);
            }}
            objectName={targetObjectName}
            id={null}
            sourcePage="detail"
            onSave={(savedData) => {
              setShowCreateModal(false);
              // Refresh the widget data
              if (onRefresh) {
                onRefresh();
              }
            }}
            onCancel={() => {
              setShowCreateModal(false);
            }}
            onRefresh={onRefresh}
            queryParams={
              currentRecordId && sourceObjectName
                ? {
                  targetField: widget.data?.targetFieldName || widget.config?.targetFieldName || widget.config?.target_field_name,
                  targetId: currentRecordId,
                  parentObjectName: sourceObjectName
                }
                : undefined
            }
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDeleteRecord}
          title="Confirm Delete"
          message="Are you sure you want to delete this record?"
          itemName={recordToDelete ? (getRecordIdValue(recordToDelete) || 'this record') : 'this record'}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Attachment Upload Modal */}
        {showAttachmentModal && (
          <Attachment
            parentId={currentRecordId}
            onUploadSuccess={uploadedFiles => {
              console.log('Files uploaded successfully:', uploadedFiles);
              // Refresh the widget data to show new files
              if (onRefresh) {
                onRefresh();
              }
              setShowAttachmentModal(false);
            }}
            onClose={() => setShowAttachmentModal(false)}
          />
        )}

        {/* File Preview Modal */}
        {showPreviewModal && previewFileRecord && (
          <FilePreviewModal
            show={showPreviewModal}
            onHide={() => {
              setShowPreviewModal(false);
              setPreviewFileRecord(null);
            }}
            fileRecord={previewFileRecord}
          />
        )}
      </>
    );
  }


  // For other widget types, render placeholder
  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: isOtherCollapsed ? 'none' : '1px solid #dee2e6',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        onClick={() => setIsOtherCollapsed(!isOtherCollapsed)}
      >
        <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 600, color: theme.colors.primary }}>
          {widget.widgetLabel || 'Widget'}
        </h5>
        <i
          className={`fas fa-chevron-${isOtherCollapsed ? 'down' : 'up'}`}
          style={{
            color: theme.colors.textSecondary,
            fontSize: '0.875rem',
            transition: 'transform 0.2s ease'
          }}
        />
      </Card.Header>
      {!isOtherCollapsed && (
        <Card.Body style={{ padding: '12px 16px' }}>
          <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
            Widget type: {widget.widgetType}
          </p>
        </Card.Body>
      )}
    </Card>
  );
};

export default LayoutWidget;
